import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Coins, Info, HardDrive, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtBytes } from "@/lib/format";
import { StatusBadge } from "@/components/common/StatusBadge";

const spend = (s) => Object.entries(s || {}).filter(([u]) => u).map(([u, v]) => `${v.toFixed(2)} ${u}`).join(" · ") || "—";
const costCell = (e) => (e.cost == null ? <span className="text-slate-500">n/a</span> : <>{e.cost.toFixed(2)} {e.unit}{e.estimated && <span className="text-amber-400" title="estimated from configured rate"> ~</span>}</>);

const StorageCard = () => {
  const [st, setSt] = useState(null);
  useEffect(() => { api.get("/costs/storage").then((r) => setSt(r.data)).catch(() => {}); }, []);
  if (!st) return null;
  const pct = Math.min(100, (st.total_bytes / st.warn_bytes) * 100);
  const seg = (b, cls) => <div className={`h-full ${cls}`} style={{ width: `${(b / st.warn_bytes) * 100}%` }} />;
  return (
    <div data-testid="storage-card" className={`panel px-4 py-3 space-y-2 ${st.warning ? "border-rose-800" : ""}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold"><HardDrive size={15} className="text-cyan-300" /> Object storage <span className="text-xs text-slate-500 font-normal">({fmtBytes(st.total_bytes)} of {fmtBytes(st.warn_bytes)} warning threshold · {pct.toFixed(0)}%)</span></div>
        {st.warning && <span data-testid="storage-warning" className="flex items-center gap-1 text-xs text-rose-300"><AlertTriangle size={13} /> Above threshold — download and clear old deliveries</span>}
      </div>
      <div className="h-2.5 rounded-full bg-[#1E2738] overflow-hidden flex">{seg(st.live_stem_bytes, "bg-cyan-400")}{seg(st.pinned_bytes, "bg-amber-400")}{seg(st.master_bytes, "bg-slate-400")}{seg(st.unreclaimed_bytes, "bg-rose-500/70")}</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        {[["Live stems", st.live_stem_bytes, st.live_stem_files, "text-cyan-300", "storage-live"], ["Pinned", st.pinned_bytes, st.pinned_files, "text-amber-300", "storage-pinned"], ["Masters", st.master_bytes, st.master_files, "text-slate-300", "storage-masters"], ["Expired, unreclaimed", st.unreclaimed_bytes, st.unreclaimed_files, "text-rose-300", "storage-unreclaimed"]]
          .map(([k, b, n, cls, tid]) => <div key={k}><div className="micro">{k}</div><div data-testid={tid} className={`font-mono ${cls}`}>{fmtBytes(b)} <span className="text-slate-500">· {n} files</span></div></div>)}
      </div>
      <div className="flex items-start gap-1.5 text-[11px] text-slate-500"><Info size={12} className="mt-0.5 shrink-0" /> {st.note}</div>
    </div>
  );
};

const RateEditor = ({ rates, onSaved }) => {
  const [v, setV] = useState(rates?.gaudio?.cost_per_minute ?? "");
  const save = async () => {
    try { await api.put("/engines/gaudio/config", { cost_per_minute: String(v) }); toast.success("Gaudio rate saved"); onSaved(); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div data-testid="costs-rate-editor" className="flex items-center gap-2 text-xs">
      <span className="micro">Gaudio USD / min / model</span>
      <input data-testid="costs-gaudio-rate-input" value={v} onChange={(e) => setV(e.target.value)} placeholder="e.g. 0.10" className="bg-[#0B0E14] border border-[#253044] rounded-sm px-2 py-1 text-xs font-mono w-24 focus:outline-none focus:border-cyan-500" />
      <button data-testid="costs-gaudio-rate-save" onClick={save} className="px-2.5 h-7 rounded-sm border border-[#3A4A68] hover:bg-[#1E2738]">Save</button>
      <span className="text-slate-500">Gaudio's API returns no cost; jobs are estimated (~) from this rate × minutes × models. AudioShake cost is read from its API.</span>
    </div>
  );
};

export default function Costs() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const load = () => api.get("/costs").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-slate-500 text-sm p-8">Loading costs…</div>;
  return (
    <div data-testid="costs-view" className="space-y-4 fade-up">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Coins size={22} className="text-amber-400" /> Costs</h1>
          <p className="text-sm text-slate-400 mt-1">Separation spend per project and episode across Gaudio (USD) and AudioShake (credits).</p>
        </div>
        <div data-testid="costs-totals" className="panel px-4 py-3 grid grid-cols-3 gap-6 text-center">
          <div><div className="micro">Completed jobs</div><div className="font-mono text-xl text-slate-100">{data.totals.jobs}</div></div>
          <div><div className="micro">Minutes processed</div><div className="font-mono text-xl text-slate-100">{data.totals.minutes}</div></div>
          <div><div className="micro">Total spend</div><div className="font-mono text-xl text-emerald-300">{spend(data.totals.spend)}</div></div>
        </div>
      </div>
      {user?.role === "admin" && <div className="panel px-4 py-3"><RateEditor rates={data.rates} onSaved={load} /></div>}
      <StorageCard />

      <div className="grid xl:grid-cols-[1fr_360px] gap-4">
        <div className="panel overflow-x-auto">
          <div className="px-4 py-2.5 micro">Per project</div>
          <table data-testid="costs-projects-table" className="w-full text-sm">
            <thead><tr className="text-left micro border-b border-[#253044]"><th className="px-3 py-2">Project</th><th className="px-3 py-2">Episodes</th><th className="px-3 py-2">Jobs</th><th className="px-3 py-2">Minutes</th><th className="px-3 py-2">Gaudio</th><th className="px-3 py-2">AudioShake</th><th className="px-3 py-2">Other</th><th className="px-3 py-2">Total spend</th></tr></thead>
            <tbody>
              {data.projects.map((p) => {
                const eng = (k) => p.by_engine[k] ? `${p.by_engine[k].cost.toFixed(2)} ${p.by_engine[k].unit || ""}${p.by_engine[k].estimated ? " ~" : ""} · ${p.by_engine[k].minutes} min` : "—";
                const other = Object.entries(p.by_engine).filter(([k]) => !["gaudio", "audioshake"].includes(k)).map(([k, v]) => `${k}: ${v.jobs} job(s)`).join(", ") || "—";
                return (
                  <tr key={p.project_id} data-testid={`costs-project-${p.project_id}`} className="row-hover border-b border-[#1c2433] last:border-0">
                    <td className="px-3 py-2 font-medium">{p.project_id}</td>
                    <td className="px-3 py-2 font-mono text-xs">{p.episodes}</td>
                    <td className="px-3 py-2 font-mono text-xs">{p.jobs}</td>
                    <td className="px-3 py-2 font-mono text-xs">{p.minutes}</td>
                    <td className="px-3 py-2 font-mono text-xs">{eng("gaudio")}</td>
                    <td className="px-3 py-2 font-mono text-xs">{eng("audioshake")}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-400">{other}</td>
                    <td className="px-3 py-2 font-mono text-xs text-emerald-300">{spend(p.spend)}{p.estimated && <span className="text-amber-400"> ~</span>}</td>
                  </tr>
                );
              })}
              {data.projects.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500 text-sm">No completed jobs yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="panel">
          <div className="px-4 py-2.5 micro">Monthly</div>
          <table data-testid="costs-monthly-table" className="w-full text-sm">
            <thead><tr className="text-left micro border-b border-[#253044]"><th className="px-3 py-2">Month</th><th className="px-3 py-2">Jobs</th><th className="px-3 py-2">Min</th><th className="px-3 py-2">Spend</th></tr></thead>
            <tbody>{data.monthly.map((m) => <tr key={m.month} className="border-b border-[#1c2433] last:border-0"><td className="px-3 py-2 font-mono text-xs">{m.month}</td><td className="px-3 py-2 font-mono text-xs">{m.jobs}</td><td className="px-3 py-2 font-mono text-xs">{m.minutes}</td><td className="px-3 py-2 font-mono text-xs text-emerald-300">{spend(m.spend)}</td></tr>)}</tbody>
          </table>
          <div className="px-4 py-3 text-[11px] text-slate-500 flex gap-2"><Info size={12} className="shrink-0 mt-0.5" /> Capacity reference: 35 episodes × 30 min ≈ 1,050 min/month. AudioShake DME ≈ 4.5 credits/min (3 models).</div>
        </div>
      </div>

      <div className="panel overflow-x-auto">
        <div className="px-4 py-2.5 micro">Per episode</div>
        <table data-testid="costs-episodes-table" className="w-full text-sm">
          <thead><tr className="text-left micro border-b border-[#253044]"><th className="px-3 py-2">Project</th><th className="px-3 py-2">Episode</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Engine</th><th className="px-3 py-2">Minutes</th><th className="px-3 py-2">Cost</th><th className="px-3 py-2">QC</th><th className="px-3 py-2">Completed</th></tr></thead>
          <tbody>
            {data.episodes.map((e) => (
              <tr key={e.job_id} data-testid={`costs-episode-${e.job_id}`} onClick={() => nav(`/qc/${e.job_id}`)} className="row-hover cursor-pointer border-b border-[#1c2433] last:border-0">
                <td className="px-3 py-2 text-xs">{e.project_id}</td>
                <td className="px-3 py-2 font-mono text-xs">{e.episode_id}</td>
                <td className="px-3 py-2 text-xs truncate max-w-[260px]">{e.source_filename}{e.benchmark_id && <span className="text-slate-500"> · benchmark</span>}</td>
                <td className="px-3 py-2 text-xs">{e.engine_name}</td>
                <td className="px-3 py-2 font-mono text-xs">{e.minutes}</td>
                <td className="px-3 py-2 font-mono text-xs">{costCell(e)}</td>
                <td className="px-3 py-2"><StatusBadge status={e.qc_status} /></td>
                <td className="px-3 py-2 text-xs text-slate-400">{fmtDate(e.processing_end)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
